<template>
  <BModal
    modal-name="bulkUpdateCustomFieldValue"
    width="550px"
    height="560px"
    :delegate="modalDelegate"
  >
    <BHeightAdjuster>
      <BModalHeader
        center
        class="modal-header"
        :title="$t('customField.bulkUpdate.title')"
        @modal-close="handleClose"
      />
      <BModalBody class="modal-body">
        <BulkUpdateModalTarget
          :label="$t('customField.bulkUpdate.target')"
          :count="selectedCallTargetIds.length"
        />
        <BListItem>
          <template #header>
            <div>{{ $t('customField.bulkUpdate.selectLabel') }}</div>
          </template>
          <BSelect
            v-model="selectedCustomFieldId"
            class="custom-field-select"
            :items="targetCustomFields"
            item-text="name"
            item-value="id"
            :placeholder="$t('customField.bulkUpdate.selectPlaceholder')"
            filterable
          >
            <template #optionTemplate="{ item }">
              <BLayout
                align-center
                class="custom-field-option"
              >
                <BDataTypeIcon :data-type="item.dataType" />
                <span class="custom-field-name truncate-text">{{ item.name }}</span>
              </BLayout>
            </template>
          </BSelect>
        </BListItem>
        <BListItem
          v-if="selectedCustomField && selectedCustomField.dataType === 'multi_option'"
          class="mt-300"
        >
          <template #header>
            <div>{{ $t('customField.bulkUpdate.modeLabel') }}</div>
          </template>
          <BLayout align-center>
            <BRadio
              v-model="mode"
              label="add"
              class="mr-300"
            >
              {{ $t('customField.bulkUpdate.modeAdd') }}
            </BRadio>
            <BRadio
              v-model="mode"
              label="delete"
            >
              {{ $t('customField.bulkUpdate.modeDelete') }}
            </BRadio>
          </BLayout>
        </BListItem>
        <BListItem
          v-if="selectedCustomField"
          class="mt-300"
        >
          <template #header>
            <div>{{ $t('customField.bulkUpdate.valueLabel') }}</div>
          </template>
          <BInput
            v-if="selectedCustomField.dataType === 'text'"
            v-model="inputValue"
            :placeholder="$t('customField.bulkUpdate.valuePlaceholder')"
            :max-length="2000"
          />
          <BNumberInput
            v-else-if="selectedCustomField.dataType === 'number'"
            v-model="inputValue"
            class="number-input"
            :placeholder="$t('customField.bulkUpdate.valuePlaceholder')"
            allow-float
          />
          <BSelect
            v-else-if="selectedCustomField.dataType === 'option'"
            v-model="selectedOptionId"
            class="value-select"
            :items="selectedCustomField.options ?? []"
            item-text="label"
            item-value="id"
            :placeholder="$t('customField.bulkUpdate.valuePlaceholderOption')"
            clearable
          />
          <BSelect
            v-else-if="selectedCustomField.dataType === 'multi_option'"
            v-model="selectedMultiOptionIds"
            class="value-select"
            :items="selectedCustomField.options ?? []"
            item-text="label"
            item-value="id"
            :placeholder="$t('customField.bulkUpdate.valuePlaceholderMultiOption')"
            multiple
            filterable
          >
            <template #optionTemplate="{ item }">
              <span class="custom-field-option-value truncate-text">{{ item.label }}</span>
            </template>
          </BSelect>
          <BLayout
            v-else-if="selectedCustomField.dataType === 'datetime'"
            align-center
          >
            <BFlex
              class="pr-100"
              :span="12"
            >
              <BDatePicker
                v-model="dateValue"
                format="MM/DD"
                clearable
                :placeholder="$t('customField.bulkUpdate.valuePlaceholderDatetime')"
              />
            </BFlex>
            <BFlex :span="12">
              <BTimePicker
                v-model="timeValue"
                format="HH:mm"
                clearable
              />
            </BFlex>
          </BLayout>
          <BLayout
            v-else-if="selectedCustomField.dataType === 'boolean'"
            align-center
            class="mt-200"
          >
            <BRadio
              v-model="booleanValue"
              :label="true"
              class="mr-300"
            >
              {{ $t('customField.boolean.true') }}
            </BRadio>
            <BRadio
              v-model="booleanValue"
              :label="false"
            >
              {{ $t('customField.boolean.false') }}
            </BRadio>
          </BLayout>
          <div
            v-if="selectedCustomField.dataType !== 'boolean' && selectedCustomField.dataType !== 'multi_option'"
            class="note mt-200"
          >
            {{ $t('customField.bulkUpdate.valueNote') }}
          </div>
          <div
            v-else-if="selectedCustomField.dataType === 'multi_option' && mode === 'add'"
            class="note mt-200"
          >
            {{ $t('customField.bulkUpdate.noteAddMultiOption') }}
          </div>
        </BListItem>
        <BulkUpdateOverLimitMessage
          :visible="isOverLimit"
          :limit="BULK_UPDATE_LIMIT"
        />
      </BModalBody>
      <BulkUpdateModalFooter
        :loading="isLoading"
        :disabled="isUpdateDisabled"
        @cancel="handleClose"
        @submit="submitBulkUpdateCustomFieldValue"
      />
    </BHeightAdjuster>
  </BModal>
</template>

<script setup lang="ts">
import { AxiosError } from 'axios';
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { CustomField as TApiCustomField, CustomFieldOption } from '@/api/openapi';
import { CallTargetApiService } from '@/api/user/resources/call_target';
import BulkUpdateModalFooter from '@/components/organisms/user/leadList/BulkUpdateModalFooter.vue';
import BulkUpdateModalTarget from '@/components/organisms/user/leadList/BulkUpdateModalTarget.vue';
import BulkUpdateOverLimitMessage from '@/components/organisms/user/leadList/BulkUpdateOverLimitMessage.vue';
import { useConfirm } from '@/composable/message-dialog';
import { useWait } from '@/composable/vue-wait';
import { KEY_OF_MODAL, KEY_OF_TOAST } from '@/injection-keys';
import { key } from '@/store/index';
import { formatDateTime, formatDateTimeWithoutSecond, mergeDateAndTime } from '@/utils/date-time';

type TProps = {
  selectedCallTargetIds: number[];
};
type TMode = 'add' | 'delete';

// NOTE: user/getCustomFieldsAction が叩く API は customFieldOptions ではなく options というキーでレスポンスを返すため、実際のレスポンス形状に合わせた型を利用する
type TCustomField = Omit<TApiCustomField, 'customFieldOptions'> & {
  options?: CustomFieldOption[];
};

const props = defineProps<TProps>();

const MODAL_NAME = 'bulkUpdateCustomFieldValue';
const BULK_UPDATE_LIMIT = 100;
const TARGET_DATA_TYPES = ['text', 'number', 'datetime', 'option', 'boolean', 'multi_option'];
const i18n = useI18n();
const store = useStore(key);
const modal = inject(KEY_OF_MODAL);
const toast = inject(KEY_OF_TOAST);
const { wait, doActionWithWait } = useWait();
const { confirm } = useConfirm();
const api = new CallTargetApiService();

const selectedCustomFieldId = ref<number | null>(null);
const inputValue = ref<string>('');
const selectedOptionId = ref<number | null>(null);
const dateValue = ref<Date | null>(null);
const timeValue = ref<Date | null>(null);
const booleanValue = ref<boolean | null>(null);
const mode = ref<TMode>('add');
const selectedMultiOptionIds = ref<number[]>([]);

const customFields = computed<TCustomField[]>(() => store.getters['user/customFields'] ?? []);
const targetCustomFields = computed<TCustomField[]>(() =>
  customFields.value.filter((customField) => TARGET_DATA_TYPES.includes(customField.dataType)),
);

onMounted(() => {
  store.dispatch('user/getCustomFieldsAction');
});

const selectedCustomField = computed<TCustomField | null>(() =>
  targetCustomFields.value.find((customField) => customField.id === selectedCustomFieldId.value) ?? null,
);

const resetValue = () => {
  inputValue.value = '';
  selectedOptionId.value = null;
  dateValue.value = null;
  timeValue.value = null;
  booleanValue.value = null;
  mode.value = 'add';
  selectedMultiOptionIds.value = [];
};

watch(selectedCustomFieldId, () => {
  resetValue();
});

const mergedDateTime = computed<Date | null>(() => mergeDateAndTime(dateValue.value, timeValue.value));

const submitValue = computed<string>(() => {
  switch (selectedCustomField.value?.dataType) {
    case 'option':
      return selectedOptionId.value != null ? String(selectedOptionId.value) : '';
    case 'datetime':
      return mergedDateTime.value ? formatDateTime(mergedDateTime.value) : '';
    case 'boolean':
      return booleanValue.value != null ? String(booleanValue.value) : '';
    default:
      return inputValue.value;
  }
});

const displayValue = computed<string>(() => {
  switch (selectedCustomField.value?.dataType) {
    case 'option':
      return (selectedCustomField.value?.options ?? []).find((option) => option.id === selectedOptionId.value)?.label ?? '';
    case 'datetime':
      return mergedDateTime.value ? formatDateTimeWithoutSecond(mergedDateTime.value) : '';
    case 'boolean':
      return booleanValue.value != null ? i18n.t(`customField.boolean.${booleanValue.value}`) : '';
    default:
      return inputValue.value;
  }
});

const selectedMultiOptionNames = computed<string>(() =>
  (selectedCustomField.value?.options ?? [])
    .filter((option) => selectedMultiOptionIds.value.includes(option.id))
    .map((option) => `「${option.label}」`)
    .join(''),
);

const isOverLimit = computed<boolean>(() => props.selectedCallTargetIds.length > BULK_UPDATE_LIMIT);
const isUpdateDisabled = computed<boolean>(() => {
  if (props.selectedCallTargetIds.length === 0 || !selectedCustomFieldId.value || isOverLimit.value) return true;
  if (selectedCustomField.value?.dataType === 'boolean' && booleanValue.value == null) return true;
  if (selectedCustomField.value?.dataType === 'multi_option' && selectedMultiOptionIds.value.length === 0) return true;
  return false;
});
const isLoading = computed<boolean>(() => wait.is(['bulkUpdateCustomFieldValueWait']));

const modalDelegate = {
  beforeClose: () => {
    selectedCustomFieldId.value = null;
    resetValue();
  },
};

const handleClose = () => {
  modal.hide(MODAL_NAME);
};

const submitBulkUpdateCustomFieldMultiOption = async () => {
  const count = props.selectedCallTargetIds.length;
  const item = selectedCustomField.value?.name ?? '';
  const confirmMessageKey = mode.value === 'add' ? 'customField.bulkUpdate.confirmAddMultiOption' : 'customField.bulkUpdate.confirmDeleteMultiOption';
  const confirmed = await confirm({
    message: i18n.t(confirmMessageKey, { count, item, value: selectedMultiOptionNames.value }),
  });
  if (!confirmed) return;

  let succeeded = false;
  await doActionWithWait('bulkUpdateCustomFieldValueWait', async () => {
    try {
      const request = {
        bulkCustomFieldMultiOptionBody: {
          callTargetIds: props.selectedCallTargetIds,
          customFieldId: selectedCustomFieldId.value,
          customFieldOptionIds: selectedMultiOptionIds.value,
        },
      };
      if (mode.value === 'add') {
        await api.bulkAppendCustomFieldMultiOption({ request, errorHandlers: { disableDefaultErrorHandler: true } });
      } else {
        await api.bulkDeleteCustomFieldMultiOption({ request, errorHandlers: { disableDefaultErrorHandler: true } });
      }
      succeeded = true;
      toast.show(i18n.t('customField.bulkUpdate.done'));
    } catch (e) {
      const message = (e as AxiosError<{ errorMessage?: string }>).response?.data?.errorMessage
        ?? i18n.t('customField.bulkUpdate.failed');
      toast.show({ message, type: 'error' });
    }
  });
  if (!succeeded) return;

  handleClose();
  await getCallList();
};

const submitBulkUpdateCustomFieldValue = async () => {
  if (selectedCustomField.value?.dataType === 'multi_option') {
    await submitBulkUpdateCustomFieldMultiOption();
    return;
  }

  const count = props.selectedCallTargetIds.length;
  const item = selectedCustomField.value?.name ?? '';
  const confirmed = await confirm({
    message: submitValue.value
      ? i18n.t('customField.bulkUpdate.confirm', { count, item, value: displayValue.value })
      : i18n.t('customField.bulkUpdate.confirmEmpty', { count, item }),
  });
  if (!confirmed) return;

  let succeeded = false;
  await doActionWithWait('bulkUpdateCustomFieldValueWait', async () => {
    try {
      await api.bulkUpdateCustomFieldValue({
        request: {
          bulkCustomFieldValueBody: {
            callTargetIds: props.selectedCallTargetIds,
            customFieldId: selectedCustomFieldId.value,
            value: submitValue.value,
          },
        },
        errorHandlers: { disableDefaultErrorHandler: true },
      });
      succeeded = true;
      toast.show(i18n.t('customField.bulkUpdate.done'));
    } catch (e) {
      const message = (e as AxiosError<{ errorMessage?: string }>).response?.data?.errorMessage
        ?? i18n.t('customField.bulkUpdate.failed');
      toast.show({ message, type: 'error' });
    }
  });
  if (!succeeded) return;

  handleClose();
  await getCallList();
};

const getCallList = async () => {
  // UX改善のため、データ再読込時に全体ローディングしないようになっている
  toast.show({ message: i18n.t('callList.update.loading'), type: 'info' });
  await doActionWithWait('getCallTargetsBackgroundWait', async () => {
    await store.dispatch('user/getCallTargetsAction');
  });
  toast.show({ message: i18n.t('callList.update.done'), type: 'success' });
};
</script>

<style lang="scss" scoped>
  .modal-body {
    padding: $basespace-600;
  }

  .custom-field-select,
  .value-select {
    width: 100%;
  }

  .value-select {
    // NOTE: 選択肢名が長い場合にタグがプルダウンの幅を超えて表示されてしまうため、幅を合わせて省略表示する
    // (flexアイテムはmax-widthだけでは内容量ぶんの最小幅を優先してしまうため、min-width: 0で明示的に縮小を許可する)
    :deep(.el-select__selected-item) {
      max-width: 100%;
      min-width: 0;
    }

    :deep(.el-tag) {
      max-width: 100%;
      min-width: 0;
    }

    :deep(.el-tag__content) {
      min-width: 0;
      overflow: hidden;
    }

    :deep(.el-select__tags-text) {
      max-width: 100%;
    }
  }

  .number-input {
    width: 100%;
    height: 40px;
    line-height: 40px;
    padding: 0 15px;

    &::placeholder {
      color: $concrete-dark;
    }
  }

  .el-select-dropdown__item {
    // NOTE: 項目名が長い場合にドロップダウンの幅が広がってしまうため上限を設ける
    .custom-field-option {
      max-width: 430px;
    }

    // NOTE: 選択肢名が長い場合にドロップダウンの幅が広がってしまうため上限を設ける
    .custom-field-option-value {
      display: block;
      max-width: 430px;
    }
  }

  .custom-field-name {
    flex: 1;
    min-width: 0;
  }

  .note {
    color: $textcolor-light;
    font-size: $fontsize-100;
  }
</style>
